import { dispatch, regEventDb, regEventFx, regFx } from 'nike-re-framejs';
import { fromJS, List } from 'immutable';

import { defaultMiddlewares } from '@/common/util';
import ticketusClient from '@/_http/axiosTicketusClient';
import { catcher } from '@/_http/utils';
import DataStateEnum from '@/common/dataState';

const LIMIT = 20;

const status = function(filter) {
    if (filter.get('status') === 'won') {
        return ['won', 'paidOut'];
    }
    if (filter.get('status') === 'all') {
        return undefined;
    }
    return [filter.get('status')];
};

function getUrlData(filter) {
    const searchById = filter.get('matchId');
    const url = searchById ? '/tickets/active-on-match' : 'tickets/mobile';
    const params = searchById
        ? { sportEventId: searchById }
        : {
              start: null,
              end: null,
              limit: LIMIT,
              status: status(filter),
              offset: filter.get('offset') || 0
          };
    return { url, params };
}

const fetchOverviewTickets = function({ filter, merge }) {
    const { url, params } = getUrlData(filter);
    const searchById = filter.get('matchId');

    ticketusClient
        .get(url, {
            params: params
        })
        .then(json => {
            dispatch(['ticketus/overview/loaded', fromJS(searchById ? { data: json.tickets } : json), merge]);
        })
        .catch(catcher.handleMasterError);
};

regEventFx('ticketus/quicktickets/initialize', defaultMiddlewares, ({ db }, [type, status, pick]) =>
    db.get('loggedIn')
        ? {
              db: db.setIn(['ticketus', 'quicktickets', 'status'], DataStateEnum.PENDING),
              'ticketus/quicktickets/load': { type, status, pick }
          }
        : { db }
);

export const clearQuickTickets = db => db.deleteIn(['ticketus', 'quicktickets']);

function filterTicketsForBetbook(json) {
    return {
        data: json.data
            .filter(
                ticket =>
                    !ticket.betStatusCounts.Cancelled &&
                    !ticket.betStatusCounts.Lost &&
                    !ticket.betStatusCounts.Returned &&
                    !ticket.betStatusCounts.Won
            )
            .slice(0, 10)
    };
}

regFx('ticketus/quicktickets/load', ({ type, status, pick }) => {
    ticketusClient
        .get('tickets/mobile', {
            params: {
                start: null,
                end: null,
                limit: status ? 20 : 10,
                offset: 0,
                type,
                status,
                pick
            }
        })
        .then(json =>
            dispatch(['ticketus/quicktickets/loaded', fromJS(status ? filterTicketsForBetbook(json) : json), false])
        )
        .catch(() => dispatch(['ticketus/quicktickets/loaded', fromJS([]), false]));
});

regEventDb('ticketus/quicktickets/loaded', defaultMiddlewares, (db, [list]) => {
    return db
        .setIn(
            ['ticketus', 'quicktickets', 'status'],
            list.get('data').size > 0 ? DataStateEnum.DATA : DataStateEnum.EMPTY
        )
        .setIn(['ticketus', 'quicktickets', 'list', 'data'], list.get('data'));
});

regFx('ticketus/overview/load', fetchOverviewTickets);

regEventFx('ticketus/overview/initialize', defaultMiddlewares, ({ db }, [matchId]) => {
    let status = db.getIn(['ticketus', 'overview', 'filter', 'status']);
    let newDb = db;
    newDb = newDb
        .updateIn(['ticketus', 'overview', 'filter', 'status'], (status = 'all') => status)
        .setIn(['ticketus', 'overview', 'filter', 'offset'], 0)
        .setIn(['ticketus', 'overview', 'filter', 'matchId'], matchId)
        .setIn(['ticketus', 'overview', 'list', 'hasNextPage'], false);

    if (status === 'Cashin') {
        return { db };
    }

    return newDb.get('loggedIn')
        ? {
              db: newDb.setIn(['ticketus', 'overview', 'pending'], true),
              'ticketus/overview/load': {
                  filter: newDb.getIn(['ticketus', 'overview', 'filter']),
                  merge: false
              }
          }
        : {
              db: newDb.setIn(['ticketus', 'overview', 'list', 'data'], List())
          };
});

regEventDb('ticketus/overview/loaded', defaultMiddlewares, (db, [list, merge]) => {
    return db
        .removeIn(['ticketus', 'overview', 'pending'])
        .setIn(['ticketus', 'overview', 'list', 'hasNextPage'], list.get('hasNextPage'))
        .updateIn(['ticketus', 'overview', 'list', 'data'], (old = List()) =>
            merge
                ? old
                      .concat(list.get('data'))
                      .toOrderedSet()
                      .toList()
                : list.get('data')
        );
});

regEventDb('ticketus/overview/over-ride-filter', defaultMiddlewares, (db, [{ status }]) => {
    return db
        .setIn(['ticketus', 'overview', 'filter', 'status'], status)
        .setIn(['ticketus', 'overview', 'filter', 'offset'], 0);
});

regEventFx('ticketus/overview/change-filter', defaultMiddlewares, ({ db }, [{ status }]) => {
    let newDb = db;
    newDb = newDb
        .setIn(['ticketus', 'overview', 'filter', 'status'], status)
        .setIn(['ticketus', 'overview', 'filter', 'offset'], 0);

    if (status === 'Cashin') {
        let cashinList = db
            .getIn(['ticketus', 'cashin', 'tickets'])
            .filter(data => data.get('status') === 'Running')
            .sort((a, b) => new Date(b.get('submitDate')).getTime() - new Date(a.get('submitDate')).getTime())
            .toList();

        return {
            db: newDb.setIn(['ticketus', 'overview', 'list', 'data'], cashinList)
        };
    } else {
        return {
            db: newDb,
            'ticketus/overview/load': {
                filter: newDb.getIn(['ticketus', 'overview', 'filter']),
                merge: false
            }
        };
    }
});

regEventFx('ticketus/overview/load-more', defaultMiddlewares, ({ db }, [_]) => {
    let status = db.getIn(['ticketus', 'overview', 'filter', 'status']);

    if (status === 'Cashin') {
        console.warn('overview: can not load-more because we load all running cashins without notAvailable');
        return { db };
    }

    if (db.getIn(['ticketus', 'overview', 'pending'])) {
        console.warn('overview: can not load-more while pending');
        return { db };
    }
    if (!db.getIn(['ticketus', 'overview', 'list', 'hasNextPage'])) {
        console.warn('overview: can not load-more without hasNextPage');
        return { db };
    }
    let newDb = db;
    newDb = newDb.updateIn(['ticketus', 'overview', 'filter', 'offset'], (offset = 0) => offset + LIMIT);

    return {
        db: newDb,
        'ticketus/overview/load': {
            filter: newDb.getIn(['ticketus', 'overview', 'filter']),
            merge: true
        }
    };
});
